import { Grid, Box } from '@mui/material';
import EditButton from '../EditButton/EditButton';
import { FC, useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import SettingDialogForm from "../Settings/SettingDialogForm";
import axios from "../../utils/axios";
import SendIcon from '@mui/icons-material/Send';
import RequestDialog, { RequestType } from './RequestDialog';

interface IUsage { used: number; limit?: number }

interface ICurrentSubscription {
  enforced: boolean;
  planCode?: string;
  paidPlanCode?: string;
  paidPlanName?: string;
  status?: string;
  endsAt?: string;
  usage?: Record<string, IUsage>;
  pos?: { planName?: string; status?: string; endsAt?: string; posLocations?: string[] };
}

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Trial',
  active: 'Active',
  grace: 'Grace period',
  expired: 'Expired',
};

const USAGE_LABEL: Record<string, string> = {
  users: 'Users',
  locations: 'Locations',
  items: 'Items',
  employees: 'Employees',
  invoicesPerMonth: 'Invoices this month',
  storageMB: 'File storage',
  ocrPerMonth: 'OCR scans this month',
};

const formatDate = (date?: string) =>
  date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const formatMB = (mb: number) => (mb >= 1024 ? `${+(mb / 1024).toFixed(1)} GB` : `${+mb.toFixed(1)} MB`);

// -1 = unlimited
const usageText = (key: string, { used, limit }: IUsage) => {
  const show = (n: number) => (key === 'storageMB' ? formatMB(n) : n.toLocaleString('en-IN'));
  return `${show(used)} / ${limit === undefined || limit === null || limit < 0 ? 'Unlimited' : show(limit)}`;
};

const titleStyle = { fontWeight: 'normal', color: '#6b7280', fontSize: 13 };
const valueStyle = { fontWeight: 'normal', color: '#000', fontSize: 14, marginBottom: 3 };

const Subscription: FC = () => {
  const [current, setCurrent] = useState<ICurrentSubscription>();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<RequestType | null>(null);
  const [requestPlan, setRequestPlan] = useState<string>();

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Plan, validity and usage of the logged-in account
  useEffect(() => {
    let active = true;
    const fetchCurrent = async () => {
      try {
        const response = await axios.get<ICurrentSubscription>('/api/v1/subscription/current');
        if (active) setCurrent(response.data);
      } catch (error: any) {
        console.log('Subscription catch block called');
      }
    };
    fetchCurrent();
    return () => { active = false; };
  }, [accountDetails?._id]);

  const expired = current?.status === 'expired';
  const fields = [
    { title: 'Plan', value: current?.paidPlanName || accountDetails?.subscription?.plan },
    { title: 'Status', value: current?.status ? STATUS_LABEL[current.status] : '-' },
    { title: current?.status === 'trialing' ? 'Trial ends on' : expired ? 'Expired on' : 'Valid till', value: formatDate(current?.endsAt) },
    { title: 'Verified Status', value: accountDetails?.verifiedStatus === 'false' ? 'No' : 'Yes' },
  ];
  const pos = current?.pos;
  const posFields = pos ? [
    { title: 'Plan', value: pos.planName },
    { title: 'Status', value: pos.status ? STATUS_LABEL[pos.status] : '-' },
    { title: pos.status === 'expired' ? 'Expired on' : 'Valid till', value: formatDate(pos.endsAt) },
    { title: 'Licensed outlets', value: (pos.posLocations || []).length },
  ] : [];
  const usage = Object.keys(USAGE_LABEL).filter((key) => current?.usage?.[key]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, m: '-10px -15px 12px', px: '15px', py: 0.5, bgcolor: '#e9ecf1', borderBottom: '1px solid #dcdcdc', borderRadius: '8px 8px 0 0' }}>
            <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, margin: 0 }}>Subscription</h5>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <EditButton size='small' label='View Plans' onClick={handleOpen} />
              <EditButton
                size='small'
                icon={<SendIcon />}
                label={current?.status === 'grace' || current?.status === 'expired' ? 'Request renewal' : 'Request upgrade'}
                onClick={() => setRequest(current?.status === 'grace' || current?.status === 'expired' ? 'renew' : 'upgrade')}
              />
            </Box>
          </Box>
          <Grid container spacing={2}>
            {fields.map(({ title, value }) => (
              <Grid item xs={6} key={title}>
                <h5 style={titleStyle}>{title}</h5>
                <h5 style={valueStyle}>{value}</h5>
              </Grid>
            ))}
          </Grid>
          {expired && (
            <h5 style={{ ...titleStyle, color: '#d32f2f', marginTop: 12 }}>
              Your plan has ended. Renew to continue using Wareozo; your data is safe.
            </h5>
          )}
          {open && <SettingDialogForm title='Subscription Plans' open={open} currentPlan={current?.paidPlanCode} onClose={handleClose} onRequest={(type, planCode) => { handleClose(); setRequestPlan(planCode); setRequest(type); }} />}
          {request && <RequestDialog type={request} planCode={requestPlan} onClose={() => { setRequest(null); setRequestPlan(undefined); }} />}
        </div>
      </Grid>
      {pos && (
        <Grid item xs={12} md={6}>
          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, m: '-10px -15px 12px', px: '15px', py: 0.5, minHeight: 36, bgcolor: '#e9ecf1', borderBottom: '1px solid #dcdcdc', borderRadius: '8px 8px 0 0' }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, margin: 0 }}>POS Subscription</h5>
              <EditButton
                size='small'
                icon={<SendIcon />}
                label={current?.pos?.status === 'grace' || current?.pos?.status === 'expired' ? 'Request renewal' : 'Request more outlets'}
                onClick={() => setRequest(current?.pos?.status === 'grace' || current?.pos?.status === 'expired' ? 'pos_renew' : 'pos')}
              />
            </Box>
            <Grid container spacing={2}>
              {posFields.map(({ title, value }) => (
                <Grid item xs={6} key={title}>
                  <h5 style={titleStyle}>{title}</h5>
                  <h5 style={valueStyle}>{value}</h5>
                </Grid>
              ))}
            </Grid>
            {pos.status === 'expired' && (
              <h5 style={{ ...titleStyle, color: '#d32f2f', marginTop: 12 }}>
                Your POS plan has ended. Renew to continue using POS; your data is safe.
              </h5>
            )}
          </div>
        </Grid>
      )}
      {usage.length > 0 && (
        <Grid item xs={12} md={6}>
          <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', m: '-10px -15px 12px', px: '15px', py: 0.5, minHeight: 36, bgcolor: '#e9ecf1', borderBottom: '1px solid #dcdcdc', borderRadius: '8px 8px 0 0' }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, margin: 0 }}>Usage</h5>
            </Box>
            <Grid container spacing={2}>
              {usage.map((key) => (
                <Grid item xs={6} key={key}>
                  <h5 style={titleStyle}>{USAGE_LABEL[key]}</h5>
                  <h5 style={valueStyle}>{usageText(key, current!.usage![key])}</h5>
                </Grid>
              ))}
            </Grid>
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export default Subscription;
